import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Tag,
  Collapse,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
// 移除 UploadProps 导入，不再需要文件上传功能
import {
  getDeviceFirmwareList,
  addDeviceFirmware,
  updateDeviceFirmware,
  deleteDeviceFirmware,
  type DeviceFirmware,
  type DeviceFirmwareAddRequest,
  type DeviceFirmwareUpdateRequest,
} from '@/api/device-firmware';
import { getProductList } from '@/api/product';
// import { formatFileSize } from '@/utils/format';

// 移除不再使用的formatFileSize函数

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

const DeviceFirmwarePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DeviceFirmware[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DeviceFirmware | null>(null);
  const [searchParams, setSearchParams] = useState({
    firmwareName: '',
    productId: undefined as number | undefined,
    isEnabled: undefined as number | undefined,
  });
  const [products, setProducts] = useState<any[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  const [form] = Form.useForm();

  // 获取产品列表
  const fetchProducts = async () => {
    try {
      const response = await getProductList({ isAll: true });
      if (response.success) {
        setProducts(response.result?.list || []);
      }
    } catch (error) {
      console.error('获取产品列表失败:', error);
    }
  };

  // 获取固件列表
  const fetchFirmwareList = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: size,
        ...searchParams,
      };
      const response = await getDeviceFirmwareList(params);
      if (response.success) {
        setDataSource(response.result?.list || []);
        setTotal(response.result?.total || 0);
      } else {
        messageApi.error(response.errorMsg || '获取固件列表失败');
      }
    } catch (error) {
      messageApi.error('获取固件列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 搜索处理
  const handleSearch = () => {
    setCurrentPage(1);
    fetchFirmwareList(1, pageSize);
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      firmwareName: '',
      productId: undefined,
      isEnabled: undefined,
    });
    setCurrentPage(1);
    fetchFirmwareList(1, pageSize);
  };

  // 添加/编辑固件
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const requestData = {
        firmware_name: values.firmwareName,
        firmware_version: parseFloat(values.firmwareVersion) || 0,
        product_id: values.productId,
        firmware_description: values.firmwareDescription || '',
        firmware_download_url: values.firmwareDownloadUrl || '',
        device_id_blacklist: values.deviceIdBlacklist || '',
        device_id_whitelist: values.deviceIdWhitelist || '',
        extension_params: values.extensionParams || '',
        is_enabled: values.isEnabled ? 1 : 0,
      };

      if (editingRecord) {
        // 编辑模式
        const updateData: DeviceFirmwareUpdateRequest = {
          ...requestData,
          id: editingRecord.id,
        };
        const response = await updateDeviceFirmware(updateData);
        if (response.success) {
          messageApi.success('更新固件成功');
          setModalVisible(false);
          fetchFirmwareList();
        } else {
          messageApi.error(response.errorMsg || '更新固件失败');
        }
      } else {
        // 新增模式
        const response = await addDeviceFirmware(requestData as DeviceFirmwareAddRequest);
        if (response.success) {
          messageApi.success('添加固件成功');
          setModalVisible(false);
          fetchFirmwareList();
        } else {
          messageApi.error(response.errorMsg || '添加固件失败');
        }
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 删除固件
  const handleDelete = async (record: DeviceFirmware) => {
    try {
      const response = await deleteDeviceFirmware(record.id);
      if (response.success) {
        messageApi.success('删除固件成功');
        fetchFirmwareList();
      } else {
        messageApi.error(response.errorMsg || '删除固件失败');
      }
    } catch (error) {
      messageApi.error('删除固件失败');
    }
  };

  // 打开添加/编辑弹窗
  const openModal = (record?: DeviceFirmware) => {
    setEditingRecord(record || null);
    if (record) {
      form.setFieldsValue({
        firmwareName: record.firmware_name,
        firmwareVersion: record.firmware_version,
        productId: record.product_id,
        firmwareDescription: record.firmware_description,
        firmwareDownloadUrl: record.firmware_download_url,
        deviceIdWhitelist: record.device_id_whitelist,
        deviceIdBlacklist: record.device_id_blacklist,
        extensionParams: record.extension_params,
        isEnabled: record.is_enabled === 1,
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 移除文件上传配置，改为直接输入下载地址

  // 表格列定义
  const columns: ColumnsType<DeviceFirmware> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '固件名称',
      dataIndex: 'firmware_name',
      key: 'firmware_name',
      width: 150,
    },
    {
      title: '固件版本',
      dataIndex: 'firmware_version',
      key: 'firmware_version',
      width: 120,
    },
    {
      title: '关联产品',
      dataIndex: 'product_id',
      key: 'product_id',
      width: 150,
      render: (productId) => {
        // 尝试字符串和数字两种匹配方式
        const product = products.find(p => String(p.id) === String(productId) || p.id === parseInt(productId));
        return product?.name || product?.productName || '-';
      },
    },
    {
      title: '固件描述',
      dataIndex: 'firmware_description',
      key: 'firmware_description',
      width: 200,
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: '下载地址',
      dataIndex: 'firmware_download_url',
      key: 'firmware_download_url',
      width: 200,
      ellipsis: true,
      render: (url) => url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          查看下载地址
        </a>
      ) : '-',
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      width: 80,
      render: (enabled) => (
        <Tag color={enabled === 1 ? 'green' : 'red'}>
          {enabled === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      width: 150,
      render: (timestamp) => {
        if (!timestamp) return '-';
        // 后端返回的是毫秒时间戳，直接使用
        const date = new Date(timestamp);
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个固件吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchProducts();
    fetchFirmwareList();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {contextHolder}
      
      {/* 搜索区域 */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={6}>
            <Input
              placeholder="请输入固件名称"
              value={searchParams.firmwareName}
              onChange={(e) => setSearchParams(prev => ({ ...prev, firmwareName: e.target.value }))}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="请选择产品"
              value={searchParams.productId}
              onChange={(value) => setSearchParams(prev => ({ ...prev, productId: value }))}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
            >
              {products.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.name || product.productName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="请选择状态"
              value={searchParams.isEnabled}
              onChange={(value) => setSearchParams(prev => ({ ...prev, isEnabled: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          添加固件
        </Button>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size || 10);
            fetchFirmwareList(page, size || 10);
          },
        }}
      />

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑固件' : '添加固件'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            label="固件名称"
            name="firmwareName"
            rules={[{ required: true, message: '请输入固件名称' }]}
          >
            <Input placeholder="请输入固件名称" />
          </Form.Item>

          <Form.Item
            label="固件版本"
            name="firmwareVersion"
            rules={[{ required: true, message: '请输入固件版本' }]}
          >
            <Input placeholder="请输入固件版本号，如：1.0" />
          </Form.Item>

          <Form.Item
            label="关联产品"
            name="productId"
            rules={[{ required: true, message: '请选择关联产品' }]}
          >
            <Select 
              placeholder="请选择关联产品"
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {products.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.name || product.productName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="固件描述"
            name="firmwareDescription"
            rules={[{ required: true, message: '请输入固件描述' }]}
          >
            <TextArea
              rows={3}
              placeholder="请输入固件描述信息"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="固件下载地址"
            name="firmwareDownloadUrl"
            rules={[
              { required: true, message: '请输入固件下载地址' },
              { type: 'url', message: '请输入有效的URL地址' },
            ]}
          >
            <Input
              placeholder="请输入固件下载地址，如：https://example.com/firmware/v1.0.0.bin"
              allowClear
            />
          </Form.Item>

          <Collapse ghost>
            <Panel header="高级设置" key="1">
              <Form.Item
                label="设备ID白名单"
                name="deviceIdWhitelist"
              >
                <TextArea
                  rows={2}
                  placeholder="请输入设备ID白名单，多个ID用逗号分隔"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="设备ID黑名单"
                name="deviceIdBlacklist"
              >
                <TextArea
                  rows={2}
                  placeholder="请输入设备ID黑名单，多个ID用逗号分隔"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="扩展参数"
                name="extensionParams"
              >
                <TextArea
                  rows={3}
                  placeholder="请输入扩展参数（JSON格式）"
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
            </Panel>
          </Collapse>

          <Form.Item
            label="启用状态"
            name="isEnabled"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch 
              checkedChildren="启用" 
              unCheckedChildren="禁用" 
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceFirmwarePage;
